import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Bike,
  Boxes,
  ClipboardList,
  Globe,
  ShoppingBag,
  Store,
  CheckCircle2,
  Cpu,
  MapPin,
  MessageSquare,
  TrendingUp,
  Users,
  Truck,
  Sparkles,
  BadgePercent,
  BellRing,
  Radar,
  ChevronDown,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  STATS,
  MARKETPLACES,
  REFERENCES,
  WHY_CARDS,
  HOW_STEPS,
  SECTORS,
  FAQS,
  FLOATING_STATUS,
  SERVICES,
  BRAND,
  HERO_VISUALS,
  whatsappUrl,
} from "@/lib/site-data";
import useSeo from "@/lib/useSeo";
import JsonLd from "@/lib/JsonLd";

const SERVICE_ICONS = {
  "pazaryeri-kurulum-yonetimi": Store,
  "uye-isyerine-ozel-siparis-sistemi": Globe,
  "kurye-cozumu": Bike,
  "fiyat-ve-stok-entegrasyonu": Cpu,
  "urun-ve-menu-yonetimi": ClipboardList,
  "dijital-operasyon-yonetimi": TrendingUp,
};

function useInView(ref, options = { threshold: 0.35 }) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      options,
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, options]);
  return inView;
}

function AnimatedNumber({ value }) {
  const match = /^([\d.,]+)(.*)$/.exec(value);
  const target = match
    ? parseFloat(match[1].replace(/\./g, "").replace(/,/g, "."))
    : NaN;
  const suffix = match ? match[2] : "";
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref);
  useEffect(() => {
    if (Number.isNaN(target)) return;
    // Fallback: if the counter never enters the viewport (e.g., user lands
    // deep on the page and the IO event races the render), snap to target
    // after 2.5s so the value is never left at 0.
    const fallback = setTimeout(() => setDisplay(target), 2500);
    return () => clearTimeout(fallback);
  }, [target]);
  useEffect(() => {
    if (!inView || Number.isNaN(target)) return;
    const start = 0;
    const duration = 1400;
    const startTs = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - startTs) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(start + (target - start) * eased);
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setDisplay(target); // hard-lock to the final value
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  if (!match) return <span ref={ref}>{value}</span>;
  const formatted =
    target >= 1000
      ? Math.round(display).toLocaleString("tr-TR")
      : Math.round(display).toString();
  return (
    <span ref={ref}>
      {Number.isNaN(target) ? value : formatted}
      {suffix}
    </span>
  );
}

export default function Home() {
  useSeo({
    title: "Epersonel | Pazaryeri, Sipariş Sistemi ve Kurye Çözümleri",
    description:
      "İşyeriniz için pazaryeri kurulumu ve yönetimi, web ve mobil sipariş sistemi, fiyat-stok entegrasyonu ve kurye çözümleri.",
  });
  return (
    <>
      <JsonLd
        id="org"
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Epersonel",
          url: "https://epersonel.biz",
          logo: (typeof window !== "undefined" ? window.location.origin : "https://epersonel.biz") + BRAND.logo,
          foundingDate: "2025",
          areaServed: "TR",
          address: {
            "@type": "PostalAddress",
            addressLocality: "İstanbul",
            addressCountry: "TR",
          },
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+90 530 133 22 05",
              contactType: "customer service",
              email: BRAND.contact.email,
              areaServed: "TR",
              availableLanguage: ["Turkish"],
            },
          ],
        }}
      />
      <JsonLd
        id="website"
        data={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          url: "https://epersonel.biz",
          name: "Epersonel",
          inLanguage: "tr-TR",
        }}
      />
      <JsonLd
        id="home-faq"
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-8 md:pt-12 lg:pt-16 pb-16 md:pb-24">
        <div className="absolute inset-0 ep-dot-pattern opacity-70 pointer-events-none" />
        <div
          aria-hidden="true"
          className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(0,242,46,0.18), transparent)" }}
        />
        <div className="ep-container relative grid min-[1200px]:grid-cols-2 gap-10 min-[1200px]:gap-12 items-center">
          <div>
            <p className="text-xs md:text-sm tracking-[0.3em] text-[#00F22E] font-medium mb-5">
              PAZARYERİ • KENDİ SİPARİŞ SİSTEMİNİZ • KURYE
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Dijital Satışın Tüm Süreci{" "}
              <span className="text-[#00F22E]">Tek Yerde.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted max-w-xl leading-relaxed">
              Pazaryeri mağazalarınızı kuruyor ve yönetiyoruz. İşyerinize özel
              web sipariş sistemini ve mobil uygulamayı hazırlıyor, ihtiyaç
              duyduğunuzda kurye çözümü sağlıyoruz.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/basvuru" className="btn-primary" data-testid="hero-cta-analysis">
                Ücretsiz Analiz İste <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/hizmetlerimiz" className="btn-secondary" data-testid="hero-cta-services">
                Hizmetleri İncele
              </Link>
            </div>
            <p className="mt-5 text-sm text-muted">
              Ücretsiz ön görüşme • İşyerine özel çözüm • Uçtan uca operasyon
              desteği
            </p>
          </div>

          {/* Mockup composition */}
          <HeroVisual />
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/[0.05] ep-section-tier-2 relative overflow-hidden">
        <div className="ep-glow-tl" aria-hidden="true" />
        <div className="ep-container py-14 md:py-20 relative">
          <h2 className="font-display text-center text-2xl md:text-3xl font-extrabold tracking-tight mb-10">
            Rakamlarla <span className="text-[#00F22E]">Epersonel</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-8 gap-x-6">
            {STATS.map((s, i) => (
              <div
                key={i}
                className="text-center px-2"
                data-testid={`stat-${i}`}
              >
                <div className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-none tabular-nums">
                  {s.isStatic ? s.value : <AnimatedNumber value={s.value} />}
                </div>
                {s.label && (
                  <div className="mt-2 text-[#00F22E] text-sm font-medium">
                    {s.label}
                  </div>
                )}
                <p className="mt-2 text-xs text-white/60 leading-snug">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKETPLACES */}
      <section className="py-16 md:py-20">
        <div className="ep-container">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Pazaryeri Başvurusundan Günlük Operasyona Kadar{" "}
              <span className="text-[#00F22E]">Yanınızdayız.</span>
            </h2>
            <p className="mt-5 text-muted text-base md:text-lg leading-relaxed">
              Epersonel, Üye İşyerlerinin Türkiye'de faaliyet gösteren
              pazaryerlerinde satışa başlaması ve mağaza süreçlerini düzenli
              biçimde yönetmesi için uçtan uca operasyon desteği sağlar.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
            {MARKETPLACES.map((m, i) => {
              const isRandevu = m.includes("Randevulu");
              return (
                <div
                  key={i}
                  data-testid={`marketplace-${i}`}
                  className="ep-card px-5 py-6 text-center flex items-center justify-center min-h-[92px] relative h-full"
                >
                  <span className="font-display font-bold text-sm md:text-base tracking-tight text-white/95 leading-snug">
                    {m}
                  </span>
                  {isRandevu && (
                    <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#00F22E]/12 text-[#00F22E] border border-[#00F22E]/40">
                      Randevulu
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-8 text-sm text-white/85 max-w-3xl leading-relaxed">
            Epersonel pazaryeri ortağı değil, Üye İşyeri operasyon partneridir.
          </p>
          <p className="mt-2 text-xs text-white/70 max-w-3xl leading-relaxed">
            Gösterilen marka adları ve varsa logoları ilgili hak sahiplerine
            aittir. Aksi açıkça belirtilmedikçe bu kullanım resmî ortaklık veya
            temsil ilişkisi anlamına gelmez.
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-16 md:py-20 ep-section-tier-2 border-y border-white/[0.05] relative overflow-hidden">
        <div className="ep-glow-br" aria-hidden="true" />
        <div className="ep-container relative">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-3xl leading-tight">
            İşyerinizin Dijital Satış İçin{" "}
            <span className="text-[#00F22E]">İhtiyaç Duyduğu Her Şey</span>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((svc) => {
              const Icon = SERVICE_ICONS[svc.slug] || Boxes;
              return (
                <Link
                  key={svc.slug}
                  to={`/hizmetlerimiz/${svc.slug}`}
                  data-testid={`service-card-${svc.slug}`}
                  className="ep-card p-7 group flex flex-col h-full focus-visible:outline-2 focus-visible:outline-[#00F22E]"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#00F22E]/10 text-[#00F22E] grid place-items-center mb-5 shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-xl font-semibold tracking-tight leading-snug">
                    {svc.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed flex-1">
                    {svc.short}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#00F22E] font-medium group-hover:gap-2.5 transition-[gap] duration-200">
                    {svc.ctaLabel} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* REFERENCES */}
      <section className="py-16 md:py-20">
        <div className="ep-container">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
              Çözüm Sunduğumuz Üye İşyerlerinden Bazıları
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed">
              Farklı sektörlerdeki işyerlerinin dijital satış kanallarını
              kuruyor, geliştiriyor ve yönetiyoruz.
            </p>
          </div>

          <div
            className="mt-10 overflow-hidden ep-marquee-container hidden sm:block"
            aria-label="Referans markalar"
            data-testid="references-strip"
          >
            <div className="flex gap-4 md:gap-5 ep-marquee w-max py-1" role="list">
              {[...REFERENCES, ...REFERENCES].map((r, i) => {
                const isDuplicate = i >= REFERENCES.length;
                return (
                  <div
                    key={i}
                    className="flex-shrink-0 w-44 md:w-52 ep-ref-card"
                    role={isDuplicate ? "presentation" : "listitem"}
                    aria-hidden={isDuplicate ? "true" : undefined}
                  >
                    <img
                      src={r.src}
                      alt={isDuplicate ? "" : `${r.name} logosu`}
                      data-shape={r.shape || "horizontal"}
                      loading="lazy"
                      decoding="async"
                      width="200"
                      height="133"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile scroll-snap slider */}
          <div
            className="mt-8 -mx-4 sm:hidden"
            aria-label="Referans markalar (mobil)"
          >
            <div className="ep-ref-mobile-slider" data-testid="references-mobile-slider">
              {REFERENCES.map((r, i) => (
                <div
                  key={i}
                  className="ep-ref-card-mobile"
                  data-testid={`references-mobile-card-${i}`}
                >
                  <img
                    src={r.src}
                    alt={`${r.name} logosu`}
                    data-shape={r.shape || "horizontal"}
                    loading={i < 4 ? "eager" : "lazy"}
                    decoding="async"
                    width="148"
                    height="112"
                  />
                </div>
              ))}
            </div>
          </div>

          <p className="mt-6 text-sm text-white/80 max-w-3xl leading-relaxed">
            Gösterilen bazı markalar bağımsız şube veya franchise Üye İşyerleri
            üzerinden temsil edilmektedir. Marka ve logolar ilgili hak sahiplerine
            aittir.
          </p>
        </div>
      </section>

      {/* WHY EPERSONEL */}
      <section className="py-16 md:py-20 ep-section-tier-2 border-y border-white/[0.05]">
        <div className="ep-container">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl leading-tight">
            Yalnızca Sistemi Kurmuyor,{" "}
            <span className="text-[#00F22E]">İşleyişini de Yönetiyoruz.</span>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CARDS.map((c, i) => (
              <div key={i} className="ep-card p-6 h-full" data-testid={`why-card-${i}`}>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#00F22E] mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-display font-semibold text-lg leading-snug">
                      {c.title}
                    </h3>
                    <p className="mt-2 text-sm text-white/70 leading-relaxed">
                      {c.copy}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="ep-hr my-14" />
          <p className="text-center font-display text-2xl md:text-3xl font-semibold tracking-tight max-w-4xl mx-auto leading-snug">
            &ldquo;Karşınızda yalnızca bir yazılım değil,{" "}
            <span className="text-[#00F22E]">
              operasyonunuzu takip eden gerçek bir ekip
            </span>{" "}
            var.&rdquo;
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="ep-container relative">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl leading-tight">
            Dijital Satışa <span className="text-[#00F22E]">Dört Adımda</span>{" "}
            Başlayın
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_STEPS.map((s, i) => (
              <div key={i} className="ep-card p-6 relative h-full flex flex-col" data-testid={`how-step-${i}`}>
                <div className="font-display text-4xl md:text-5xl font-extrabold text-[#00F22E] mb-3 leading-none">
                  {s.n}
                </div>
                <h3 className="font-display font-semibold text-lg leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">
                  {s.copy}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/basvuru" className="btn-primary" data-testid="how-cta">
              İlk Adımı Atın <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-16 md:py-20 ep-section-tier-2 border-y border-white/[0.05]">
        <div className="ep-container">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight max-w-3xl leading-tight">
            Her İşyerinin Dijital Satış İhtiyacı Farklıdır.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SECTORS.map((s, i) => (
              <div key={i} className="ep-card p-6 h-full" data-testid={`sector-${i}`}>
                <h3 className="font-display font-semibold text-lg leading-snug">{s.name}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/basvuru" className="btn-secondary" data-testid="sectors-cta">
              Sektörüme Uygun Çözümü Bul
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20">
        <div className="ep-container max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Sık Sorulan Sorular
          </h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-white/[0.08]"
              >
                <AccordionTrigger
                  className="text-left font-display text-base md:text-lg font-medium hover:text-[#00F22E] hover:no-underline"
                  data-testid={`faq-question-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 md:py-16 ep-section-tier-2 border-y border-white/[0.05]">
        <div className="ep-container">
          <div className="ep-card p-8 md:p-10 relative overflow-hidden">
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none"
              style={{ background: "radial-gradient(closest-side, rgba(0,242,46,0.14), transparent)" }}
            />
            <div className="relative max-w-3xl">
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                İşyerinizin Dijital Satış Yolculuğunu{" "}
                <span className="text-[#00F22E]">Birlikte Başlatalım.</span>
              </h2>
              <p className="mt-5 text-muted text-base md:text-lg leading-relaxed">
                Pazaryerlerinde yer alın, kendi web ve mobil sipariş kanalınızı
                oluşturun ve ihtiyaç duyduğunuzda kurye çözümünden yararlanın.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/basvuru" className="btn-primary" data-testid="final-cta-analysis">
                  Ücretsiz Analiz İste
                </Link>
                <a
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  data-testid="final-cta-whatsapp"
                >
                  WhatsApp'tan Görüşelim
                </a>
              </div>
              <p className="mt-5 text-white/80 text-base md:text-lg leading-relaxed">
                Formu doldurun, ekibimiz işyerinizin ihtiyaçlarını değerlendirmek
                için sizinle iletişime geçsin.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function HeroVisual() {
  return (
    <div className="ep-hero-viz ep-fade-up" data-testid="hero-visual">
      {/* Left shopping phone — counter-clockwise tilt */}
      <img
        src={HERO_VISUALS.groceryPhone}
        alt="Emek Market mobil sipariş uygulamasında ürün kategorileri ve toplu alım kampanyası"
        className="ep-hero-phone ep-hero-phone-a"
        loading="eager"
        fetchPriority="high"
        width="700"
        height="1400"
        data-testid="hero-phone-grocery"
      />
      {/* Right tracking phone — clockwise tilt */}
      <img
        src={HERO_VISUALS.trackingPhone}
        alt="Emek Market siparişi hazırlanıyor ve kurye takibi ekranı"
        className="ep-hero-phone ep-hero-phone-b"
        loading="eager"
        width="700"
        height="1400"
        data-testid="hero-phone-tracking"
      />
      {/* Notifications — each positioned relative to the wrapper so they do
          not inherit the phone rotations/animations. */}
      <img
        src={HERO_VISUALS.notifNewOrder}
        alt="Yeni sipariş alındı bildirimi"
        className="ep-hero-notif ep-hero-notif-neworder"
        loading="lazy"
        width="600"
        height="180"
        data-testid="hero-notif-1"
      />
      <img
        src={HERO_VISUALS.notifPayment}
        alt="Ödeme tamamlandı bildirimi"
        className="ep-hero-notif ep-hero-notif-payment"
        loading="lazy"
        width="600"
        height="180"
        data-testid="hero-notif-2"
      />
      <img
        src={HERO_VISUALS.notifCourier}
        alt="Kurye yönlendirildi bildirimi"
        className="ep-hero-notif ep-hero-notif-courier"
        loading="lazy"
        width="600"
        height="180"
        data-testid="hero-notif-3"
      />
      <img
        src={HERO_VISUALS.notifDelivered}
        alt="Sipariş teslim edildi bildirimi"
        className="ep-hero-notif ep-hero-notif-delivered"
        loading="lazy"
        width="600"
        height="180"
        data-testid="hero-notif-4"
      />
    </div>
  );
}

function HeroMockup() {
  return (
    <div className="relative aspect-[5/4] w-full max-w-[560px] mx-auto lg:ml-auto">
      {/* Desktop mockup */}
      <div
        className="absolute inset-0 rounded-2xl overflow-hidden border border-white/10 bg-[#0e120f] shadow-2xl"
        aria-hidden="true"
        data-testid="hero-desktop-mockup"
      >
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/[0.06] bg-black/40">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          <div className="ml-3 flex-1 rounded-md bg-white/[0.04] h-5" />
        </div>
        <div className="p-5 grid grid-cols-3 gap-3">
          <div className="col-span-2 space-y-3">
            <div className="h-6 w-2/3 rounded bg-white/[0.06]" />
            <div className="h-3 w-11/12 rounded bg-white/[0.04]" />
            <div className="grid grid-cols-3 gap-2 pt-2">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/[0.04] relative overflow-hidden"
                >
                  <div className="absolute bottom-1.5 left-1.5 right-1.5 h-2 rounded bg-white/[0.05]" />
                  {i % 2 === 0 && (
                    <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#00F22E]" />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <div className="rounded-lg border border-[#00F22E]/25 bg-[#00F22E]/[0.06] px-3 py-2">
              <div className="text-[10px] text-[#00F22E] font-medium">Sipariş</div>
              <div className="mt-1 h-3 w-8 rounded bg-white/[0.08]" />
            </div>
            <div className="space-y-1.5">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-white/[0.06]" />
                  <div
                    className="flex-1 h-2 rounded bg-white/[0.04]"
                    style={{ maxWidth: `${70 + ((i * 7) % 25)}%` }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Phone mockup */}
      <div
        className="absolute -bottom-6 -left-4 sm:left-0 w-[42%] aspect-[9/19] rounded-[28px] border border-white/10 bg-[#0a0c0a] shadow-2xl overflow-hidden"
        aria-hidden="true"
        data-testid="hero-phone-mockup"
      >
        <div className="h-6 bg-black flex items-center justify-center">
          <div className="w-16 h-1.5 rounded-full bg-white/10" />
        </div>
        <div className="px-3 py-3 space-y-2.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-[#00F22E]" />
            <div className="h-2.5 w-16 rounded bg-white/[0.08]" />
          </div>
          <div className="h-16 rounded-xl bg-gradient-to-br from-[#00F22E]/15 to-transparent border border-[#00F22E]/20 p-2 flex flex-col justify-end">
            <div className="h-2 w-10 rounded bg-white/[0.08]" />
            <div className="mt-1 h-2 w-14 rounded bg-white/[0.05]" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-xl bg-white/[0.04] border border-white/[0.04]"
              />
            ))}
          </div>
          <div className="h-9 rounded-xl bg-[#00F22E] grid place-items-center text-[10px] font-bold text-black">
            Sipariş Ver
          </div>
        </div>
      </div>

      {/* Floating status cards */}
      <div
        className="absolute -top-6 -right-2 sm:-right-6 ep-glass px-3 py-2 rounded-xl ep-float"
        role="status"
        data-testid="hero-status-1"
      >
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-[#00F22E]" />
          <span className="text-xs font-medium">Yeni sipariş alındı</span>
        </div>
      </div>
      <div
        className="absolute top-1/3 -right-4 ep-glass px-3 py-2 rounded-xl ep-float-delayed hidden sm:flex"
        role="status"
        data-testid="hero-status-2"
      >
        <div className="flex items-center gap-2">
          <BadgePercent className="h-4 w-4 text-[#00F22E]" />
          <span className="text-xs font-medium">Kampanya aktif</span>
        </div>
      </div>
      <div
        className="absolute -bottom-2 right-8 ep-glass px-3 py-2 rounded-xl ep-float"
        role="status"
        data-testid="hero-status-3"
      >
        <div className="flex items-center gap-2">
          <Radar className="h-4 w-4 text-[#00F22E]" />
          <span className="text-xs font-medium">Kurye yönlendirildi</span>
        </div>
      </div>
    </div>
  );
}
